import { useTranslation } from 'react-i18next';
import { useNav, type Tab } from '../store/nav';
import { haptic } from '../hooks/useTelegram';
import { FigIcon, type FigIconName } from './FigIcon';
import { getIdentity } from '../lib/identity';

// Figma's footer (node 2:9227/2:9228) read through the token layer: the bar is
// a translucent sheet over the page, so it has to change ground with the theme
// or it stays a white slab in a dark app.
const NAV_BG = 'rgb(var(--nav) / 0.76)';
const SELECTED_BG = 'rgb(var(--press) / 0.08)';
// Read from the token layer so the bar follows the theme; these go into
// inline styles and a box-shadow, where a utility class cannot reach.
const ACTIVE = 'rgb(var(--accent))';
const INACTIVE = 'rgb(var(--ink))';
const NAV_SHADOW = 'var(--nav-shadow)';
const QR_BG = 'rgba(33, 149, 241, 0.72)';

export function BottomNav() {
  const { t } = useTranslation();
  const tab = useNav((s) => s.tab);
  const setTab = useNav((s) => s.setTab);
  const push = useNav((s) => s.push);

  const item = (key: Tab, label: string, icon: (active: boolean) => React.ReactNode) => {
    const active = tab === key;
    return (
      <button
        key={key}
        onClick={() => {
          haptic();
          setTab(key);
        }}
        className="flex h-[54px] flex-1 flex-col items-center justify-center gap-[2px] rounded-full"
        style={active ? { backgroundColor: SELECTED_BG } : undefined}
      >
        {/* fixed 26px slot so every icon (glyph or avatar) shares one baseline */}
        <span
          className="flex size-[26px] items-center justify-center"
          style={{ color: active ? ACTIVE : INACTIVE }}
        >
          {icon(active)}
        </span>
        <span
          className="text-[11px] font-medium leading-none tracking-[-0.3px]"
          style={{ color: active ? ACTIVE : INACTIVE }}
        >
          {label}
        </span>
      </button>
    );
  };

  const glyph = (name: FigIconName) => () => <FigIcon name={name} size={26} />;

  // Profile tab renders the Telegram avatar (24px circle, Figma); when active it
  // gets a blue ring. Falls back to the name's initial when there's no photo.
  const { photo, initial } = getIdentity();
  const avatar = (active: boolean) => (
    <span
      className="grid size-[24px] place-items-center overflow-hidden rounded-full bg-gradient-to-br from-[#5cb6f8] to-[#2152f1] text-[12px] font-semibold text-white"
      style={active ? { boxShadow: `0 0 0 2px ${ACTIVE}` } : undefined}
    >
      {photo ? (
        <img src={photo} alt="" className="h-full w-full object-cover" />
      ) : (
        initial
      )}
    </span>
  );

  return (
    <div className="pointer-events-none fixed bottom-0 left-1/2 z-40 flex w-full max-w-[440px] -translate-x-1/2 items-center gap-[10px] px-5 pb-[max(21px,var(--tg-safe-bottom))] pt-[21px]">
      <nav
        className="pointer-events-auto flex flex-1 items-center rounded-full p-[5px] backdrop-blur-[2px]"
        style={{ backgroundColor: NAV_BG, boxShadow: NAV_SHADOW }}
      >
        {item('wallet', t('nav.wallet'), glyph('navWallet'))}
        {item('cards', t('nav.cards'), glyph('navCards'))}
        {item('profile', t('nav.profile'), avatar)}
      </nav>
      <button
        onClick={() => {
          haptic('medium');
          push({ name: 'qr' });
        }}
        aria-label={t('wallet.qr')}
        className="pointer-events-auto grid size-[64px] shrink-0 place-items-center rounded-full text-white transition-transform active:scale-95"
        style={{ backgroundColor: QR_BG, boxShadow: NAV_SHADOW }}
      >
        <FigIcon name="qrFill" size={28} className="text-white" />
      </button>
    </div>
  );
}
